import React from 'react';
import { Home, ChevronRight, Volume2, Users, ArrowRight, ArrowLeft } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface AnnieGibbSectionProps {
  onSelectPart?: (partId: string) => void;
}

export const AnnieGibbSection: React.FC<AnnieGibbSectionProps> = ({ onSelectPart }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="space-y-10 font-sans text-[#2c221e]">
      
      {/* Sub-navigation Breadcrumbs & Voice Narrator Controls */}
      <header className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          
          {/* Breadcrumb: The Dentons > Part 5 of 9: Annie Denton nee Gibb */}
          <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 sm:gap-2">
            <button
              onClick={() => onSelectPart && onSelectPart('hub')}
              className="hover:underline flex items-center gap-1 font-bold text-[#7a5b3a]"
            >
              <Home className="w-3.5 h-3.5" />
              <span>The Dentons</span>
            </button>
            <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full font-bold uppercase tracking-wider text-[#7a5b3a] text-[11px] sm:text-xs">
              Part 5 of 9: Annie Denton nee Gibb
            </span>
          </nav>

          {/* Audio Narration Trigger */}
          <button
            onClick={speakCurrentPage}
            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs cursor-pointer"
            title="Listen to The Story of Young Annie Gibb"
          >
            <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
            <span>{isPlaying ? 'Listening to Section...' : 'Listen to this Chapter'}</span>
          </button>
        </div>

        <h1 className="font-serif text-3xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight leading-tight pt-1">
          THE STORY OF YOUNG ANNIE GIBB
        </h1>

        <p className="font-serif text-lg md:text-xl font-bold text-[#7a5b3a] italic leading-snug">
          as recorded by her second daughter, Margaret
        </p>

        <div className="flex items-center gap-2 text-xs font-serif text-[#7a5b3a] uppercase tracking-wider font-semibold">
          <Users className="w-3.5 h-3.5" />
          <span>Part 5 of the 9-Part Denton Family History</span>
        </div>
      </header>

      {/* Main Archival Text Content */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-8 text-base md:text-lg leading-relaxed text-[#2c221e]">
        
        {/* Paragraph 1 */}
        <p>
          Annie’s parents, James Lindsay Gibb and Eliza Catherine (nee Roberts) married on 24 December 1891, in a Christmas Eve celebration at Happy Valley near Motunau in North Canterbury. Their courtship had lasted five years, beginning when Eliza was 17 and James 28. The very respected Rev William Rickarby Campbell of Waiau performed the ceremony and the newlyweds left in a four wheeled buggy.
        </p>

        {/* Image 70 */}
        <div className="my-6">
          <ArchivalImageCard
            src="/images/70 1891 Annie_s parents Lizzie and James Gibb.jpg"
            alt="1891 Annie's parents Lizzie and James Gibb"
            caption="Lizzie and Jim, 1891"
          />
        </div>

        {/* Paragraph 2 */}
        <p>
          They moved into a new wooden house on their farm Heathfield, unfinished at that time, with the living room still to be lined. Lizzie told how they walked up to the original thatched sod hut that James had built when he bought the farm on Christmas Day, the day after their wedding.
        </p>

        {/* Image 71 */}
        <div className="my-6">
          <ArchivalImageCard
            src="/images/71 Towards Heathfield in winter 2021.jpg"
            alt="Towards Heathfield in winter 2021"
            caption="Winter 2021 looking along Heathfield Road towards the farm – not looking as good in earlier days"
          />
        </div>

        {/* Paragraph 3 */}
        <p>
          Their first child was born at Sefton nearly a year later, 12 December 1892, named Helen Isabel.
        </p>

        {/* Paragraph 4 */}
        <p>
          On 21 June 1893 Eliza’s younger sister Jane married James’ brother, Stewart Burnett Gibb. The much-loved Rev Campbell took the ceremony at Heathfield in front of the open fire in the kitchen with its colonial oven, followed by a dance at the now-finished house. The couple left for their home at Glen Clova on Gibbs Road the next day in a spring dray on a winter’s morning. Their house contained one bedroom, a kitchen with a coal range and a lean-to pantry, constructed so extra rooms could be added. A bedroom was soon added.
        </p>

        {/* Paragraph 5 */}
        <p>
          Eliza’s brother Baxter had been living with James and Eliza since June 1893 so he could attend the newly opened school at Greta Valley – a 5 km walk away. A younger brother Edward came to live at Heathfield in December 1893 so he too could attend school when Baxter went home to help his father.
        </p>

        {/* Paragraph 6 */}
        <p>
          Eliza gave birth to her second daughter, Margaret Annie, on 28 March 1894, at the home of Fanniah and James Pain in Sefton. The baby weighed less than 5 lbs (2.27 kg). Fanniah Pain was Eliza’s half aunt, being the half-sister of Sarah Elizabeth Roberts, Eliza’s mother (who had six children to her second husband, Allan Bunting). Eliza had her 15-month-old daughter Helen with her.
        </p>

        {/* Paragraph 7 */}
        <p>
          On the 13th day after the birth, with some help from the Pains, Eliza walked to the coach which took her to her mother-in-law Helen Gibb. Helen ran an accommodation house at Cabbage Tree Flat, on the main highway north of Greta Valley. Helen looked after the ill Eliza, young Helen, and the small baby, who was baptised at her grandmother’s house.
        </p>

        {/* Paragraph 8 */}
        <p>
          Helen Gibb planted the magnificent oak trees that line the road north of Greta Valley<sup className="font-bold text-[#7a5b3a]">1</sup>.
        </p>

        {/* Paragraph 9 */}
        <p>
          Metaphorically, she also began a New Zealand family that spread its roots through the region.
        </p>

        {/* Paragraph 10 */}
        <p>
          One of the visitors to see the new baby was Eliza Jane Denton, who rode side saddle 9.5 km miles while pregnant with her sixth child, Ivy. These families would be joined in marriage 25 years later when that little Annie would grow up to marry Eliza’s third child, John Thomas, then aged 8.
        </p>

        {/* Footnote 1 */}
        <div className="bg-[#f3ede0] p-4 rounded-xl border border-[#d8ceb9] text-sm text-[#5c4328] my-2">
          <p>
            <sup className="font-bold">1</sup> Pictured on the covers
          </p>
        </div>

        {/* Image 72 */}
        <div className="my-6">
          <ArchivalImageCard
            src="/images/72 Eliza Denton, Tommy_s mother, with Ivy 1894.jpg"
            alt="Eliza Denton, Tommy's mother, with Ivy 1894"
            caption="Eliza Denton with infant Ivy, 1894"
          />
        </div>

        {/* Paragraph 11 */}
        <p>
          In the autumn of 1894 Jack and Eliza Denton took up a farm section at Domett, which Eliza had won in the Cheviot Settlement ballot. It was the start of the government’s breaking up of huge estates into smaller farms for working farmers.
        </p>

        {/* Paragraph 12 */}
        <p>
          In November 1894 Edward moved to live with Helen Gibb so he could continue at school. George Lindsay Roberts (eight and a half), Eliza’s next brother, moved to Heathfield to attend school.
        </p>

        {/* Paragraph 13 */}
        <p>
          Jim and Lizzie put on a Christmas party at Heathfield in 1895. Such large family gatherings were frequent and fondly remembered by Annie, who was blessed with a wonderful memory.
        </p>

        {/* Paragraph 14 */}
        <p>
          Jim had a contract to cart bales of wool from local farmers with his wagon and team of six horses, so he was often away for several days. Lizzie was pregnant with her third child in late 1896 and could not be left alone as it was about two miles (about 3 km) over a rough uphill road to her nearest neighbour, a Mrs McLelland. Lizzie and her two daughters went her parent’s home Plas Isa near Scargill, about 11 km away, to await the birth.
        </p>

        {/* Image 73 */}
        <div className="my-6">
          <ArchivalImageCard
            src="/images/73 James Gibb_s horse team.jpg"
            alt="James Gibb's horse team"
            caption="Jim Gibb’s horse team he used for contracting in the district"
          />
        </div>

        {/* Paragraph 15 */}
        <p>
          Annie could remember sleeping with her mother while Helen slept in another bed beside them. On the morning of 5 October Annie woke to find her mother missing. One of her aunts carried her to another room to see the new baby. She recalled how they said “won’t Jimmy be pleased to have a son”.
        </p>

        {/* Paragraph 16 */}
        <p>
          That baby was named Robert Morris Stewart after his grandfather Robert Morris Roberts as he was born on his 54th birthday, and on his uncle Robert Morris’ 22nd birthday. He was always called Morris.
        </p>

        {/* Paragraph 17 */}
        <p>
          Helen and Annie asked Mrs Cameron, the midwife, where the baby had come from. Mrs Cameron said she had fetched him from the riverbed during the night – so the two little girls went off to look for another one of the baby boys as they could see how loved he already was.
        </p>

        {/* Paragraph 18 */}
        <p>
          Annie recalls trying to get her grandfather’s paintbrush as he was painting the house. Her aunts Margaret, Mary and Fanny growled at her and threatened to spank her – she said she never forgot that!
        </p>

        {/* Paragraph 19 */}
        <p>
          The Roberts family had moved to the Waikari Valley near Scargill in 1890, from Happy Valley where they had lived on the Tully farm for six years. The name Plas Isa came from the Roberts family North Wales home.
        </p>

        {/* Paragraph 20 */}
        <p>
          Annie and her brother Morris were always close friends as children and spent a lot of time walking over the hills on the farm, listening to the frogs, watching out for birds and wild pigs, getting to know the land in great detail. It seems they were stronger than Helen, their older sister. Annie was also very fond of her cousin, Burnie Gibb – the first child of Stewart and Jane and a fortnight younger than Annie. She deeply grieved when he was killed in action, Messines Ridge, 7 August 1917 aged 23.
        </p>

        {/* Image 74 */}
        <div className="my-6">
          <ArchivalImageCard
            src="/images/74 Burnie Gibb.jpg"
            alt="Burnie Gibb"
            caption="Burnie Gibb"
          />
        </div>

        {/* Paragraph 21 */}
        <p>
          The first photograph of Annie in the family album was taken of pupils at the Greta Valley School, in 1899 when Annie was not quite five. She was taken to school for that special occasion. She and Helen went to school one or two days a week, walking the 5 km over paddocks, through a gully sometimes with a stream running in it, then up another hill and onto a shingle road.
        </p>

        {/* Image 75 */}
        <div className="my-6">
          <ArchivalImageCard
            src="/images/75 1901 Greta Valley School.jpg"
            alt="1901 Greta Valley School"
            caption="Helen and Annie are the two girls standing together in front, Helen in white apron and Annie (aged 4) glancing sideways – looking towards her cousin Burnie as she recalled."
          />
        </div>

        {/* Paragraph 22 */}
        <p>
          The school was opened on 26 June 1893, celebrated by a high tea and a dance at Tipapa. The land had been donated by William Acton-Adams. Until then children were taught at home or sent to the nearest schools at Sefton, Kimberley, Southbrook and Waimate. Mr Theophilus Samuel Mannering of Greta Vale was superintendent and teacher at the Sunday School and he was largely responsible for getting the Education Board to agree to a school. First day pupils included Margaret, Jack, Tom and Harry Denton and Baxter Roberts. The first teacher was Miss Claudia Watson<sup className="font-bold text-[#7a5b3a]">2</sup>, who arranged three school picnics in December – the first at Glen Dhu bush, the second on the south side of the creek over the Motunau Hill and the third at the foot of Foster’s Cutting.
        </p>

        {/* Paragraph 23 */}
        <p>
          Prizegiving was a social event for the district, with a concert and a very good dance and supper, with someone playing the piano accordion or a violin. The first moving pictures were shown in the school in summer 1898.
        </p>

        {/* Image 76 */}
        <div className="my-6">
          <ArchivalImageCard
            src="/images/76 Morris, Helen and Annie Gibb 1901.jpg"
            alt="Morris, Helen and Annie Gibb 1901"
            caption="Morris (5), Helen (8.5), Annie (7)"
          />
        </div>

        {/* Paragraph 24 */}
        <p>
          Annie’s cousin T.E.L. Roberts<sup className="font-bold text-[#7a5b3a]">3</sup> wrote: “One of the keenest memories of ex-pupils is of the hive of bees which had possession of the north cornice of the school and which distracted greatly from the eagerness of the more adventurous bird-nesters who tried to increase their wealth by robbing the birds’ nests which were to be seen in large numbers in the belfry, and also prevented any efforts of anyone trying to mend the bell ropes, so the bell had to remain out of commission for some time. Efforts were made to dislodge the bees, with varying results on both sides. Casualties were not all one the bee’s side.”
        </p>

        {/* Image 77 */}
        <div className="my-6">
          <ArchivalImageCard
            src="/images/77 Greta Valley School photo.jpg"
            alt="Greta Valley School photo"
            caption="See how the trees have grown in the few years between the photos."
          />
        </div>

        {/* Paragraph 25 */}
        <p>
          Annie had her first holiday in 1899. Her aunt Ella Gibb, who was partially deaf, lived with her mother Helen and helped with the accommodation and paddocks. Helen Gibb had gone to Hanmer Springs for a holiday and Ella needed to have someone in the house to hear travellers coming to the house. Ella went to Heathfield intending to ask Helen to stay but she began to cry, took fright and ran away. Thus it was Annie who went to Cabbage Tree Flat, after being washed, packed up and put in the gig that Ella drove. Annie enjoyed this time and even at that age she understood deafness as her father had been very deaf from the age of 18 months. In later life James got one of the first hearing aids and heard a cat purr for the first time in his life. He sat under the trees just to hear the birds singing.
        </p>

        {/* Footnotes 2 & 3 */}
        <div className="bg-[#f3ede0] p-4 rounded-xl border border-[#d8ceb9] text-sm text-[#5c4328] space-y-1 my-2">
          <p>
            <sup className="font-bold">2</sup> 1893-93, when she married a Methodist Home Missionary, Mr Sommerville
          </p>
          <p>
            <sup className="font-bold">3</sup> Taken from “Motunau Memories”
          </p>
        </div>

        {/* Paragraph 26 */}
        <p>
          During Annie’s early years she would stay one night at least a week at Cabbage Tree Flat as it was only half a mile from the school, saving her the long walk to and from school.
        </p>

        {/* Image 78 */}
        <div className="my-6">
          <ArchivalImageCard
            src="/images/78 Ella and Helen Gibb at Cabbage tree Flat.jpg"
            alt="Ella and Helen Gibb at Cabbage tree Flat"
            caption="Cabbage Tree Flat, Ella on the left, and Helen Gibb"
          />
        </div>

        {/* Paragraph 27 */}
        <p>
          Christmas 1899 was a big event at Heathfield. On Christmas Eve a dray drawn by three horses and loaded with members of the extended Roberts family arrived to sleep the night at Heathfield. Edward Roberts rode a horse alongside the dray. On Christmas Day all the relatives gathered at Heathfield and drove out to Motunau Beach for a picnic. Stewart and Jane Gibb and their four children were there. James’ younger brother George, who was living at Cabbage Tree Flat, rode his horse out to the coast. Picnics were very popular family and community events, with large hampers of food, pies, cakes, sandwiches, often an open fire to boil the billy for tea. It was a time for relaxation, games for the children, and catching up on the news of family and friends.
        </p>

        {/* Paragraph 28 */}
        <p>
          They returned to Heathfield for an evening party in the big sitting room which Eliza and Annie had cleaned out a few days earlier so there could be dancing on the good wooden floor dancing while George Wornall played his violin. Annie remembered how she helped her mother clean out the room for the party. (George was an Amberley farmer who played at many social and community events - he died in 1958.)
        </p>

        {/* Paragraph 29 */}
        <p>
          In the following years Christmas day was celebrated at Plas Isa, with buggies and riders happily making their way inland. When they reached the top of the Motunau Hill they could see if Stewart and Jane Gibb were on their way. Their son Burnie (Burnett Stewart Gibb) rode a horse beside their buggy and then later a bicycle.
        </p>

        {/* Paragraph 30 */}
        <p>
          When she was about nine Annie remembered there was snow on Christmas Day and it was very disappointing to have to stay at home. (She loved those family events, and in later life often hosted them for her own family at Oakdale.)
        </p>

        {/* Paragraph 31 */}
        <p>
          New Years’ Day was another big family and community event. The School Committee held a picnic at Motunau with plenty of games for the children. Families came from as far away as Scargill, some 22 km from the beach.
        </p>

        {/* Sourced note */}
        <div className="bg-[#f3ede0] p-4 rounded-xl border border-[#d8ceb9] text-sm text-[#5c4328] italic my-2">
          <p>
            This was sourced from notes Margaret Quigley made while talking to her mother,Annie, then in her 90s
          </p>
        </div>

        {/* Paragraph 32 */}
        <p>
          Annie recalled that her grandmothers were quite different. She said that when she got a bee sting Helen Gibb would say “well, it serves you right” or “it was your fault” while her great Grandmother Margaret Bunting would be full of sympathy and put something on it to ease the pain. Margaret was a popular midwife in the Sefton area, and grandmother of Annie’s mother, Eliza.
        </p>

        {/* Paragraph 33 */}
        <p>
          The landscape that Annie grew up in is still stunning. The limestone country is so interesting, with much variety and contrast.
        </p>

        {/* Images 79 & 80 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
          <ArchivalImageCard
            src="/images/79 Near heathfield.jpg"
            alt="Near Heathfield"
            caption="Near Heathfield"
          />
          <ArchivalImageCard
            src="/images/80 Along Motunau Beach Road.jpg"
            alt="Along Motunau Beach Road"
            caption="Along Motunau Beach Road"
          />
        </div>

        {/* Images 81 & 82 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
          <ArchivalImageCard
            src="/images/81 Annie Denton cutting.jpg"
            alt="Annie Denton cutting ribbon"
            caption="Annie Denton (aged 97) cut the ribbon as part of the Official Opening of the Greta Valley Walkway 1991"
          />
          <ArchivalImageCard
            src="/images/82 Annie_s last visit to Heathfield, aged 97, in 1991.jpg"
            alt="Annie's last visit to Heathfield, aged 97, in 1991"
            caption="Annie made a final visit to Heathfield in 1991"
          />
        </div>

        {/* Section Divider: Edward Roberts' Story */}
        <div className="pt-6 border-t-2 border-[#d8ceb9] space-y-6">
          <p className="font-bold text-[#4a3525]">
            Edward Roberts<sup className="font-bold text-[#7a5b3a]">4</sup> had this story to tell about living with Lizzie and Jim Gibb.
          </p>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] text-sm md:text-base leading-relaxed text-[#2c221e] italic space-y-4">
            <p>
              “In February 1894 I owed Miss Watson a few shillings for schoolbooks she supplied me with when the school re-opened on 28 January after the summer holidays. I understood Jim Gibb was to pay for my schoolbooks while I went to school from Heathfield. When I asked my sister Lizzie for money to pay for the books, she said the only money in the house was a cheque. Miss Watson kept asking for payment and this worried me. I thought I might sell a watch chain and raise the money and gave it to Jim McLelland to take it home to see if his father would consent to Jim buying it. Jim handed me the chain next day and said his father said it was not worth anything, so I was still worried.
            </p>
            <p>
              Within a few days my fortune changed. Only Jim and Leonard McLelland came to school on Gypsy. On the way home from school I was getting a help along the road by clinging to the tail of Gypsy and about halfway between the gate across the road in front of Tipapa and the Motunau Hill I saw something bright lying on the road and stopped to pick it up. It was a gold coin and I wondered if it was a sovereign or half sovereign. The McLelland boys said they saw it on their way to school in the morning but did not have time to pick it up. However I was older and stronger at the time than the McLelland boys and I kept possession. Next morning I handed it to Miss Watson and settled the debt. It was a half sovereign and I got more than half the value back in change. I have never since worried so much over finance, though at times I have owed several hundred pounds, but have always been in a position to meet the debt.”
            </p>
          </blockquote>

          <p>
            Helen Gibb wanted Edward to go and live at her place from October 1894 and although Jim didn’t want him to go, he didn’t want to disappoint his mother, so Lizzie wrote a letter to her parents requesting consent for the change….” And I walked home with it and got their consent to make the change. A few days later my sister Margaret walked from Happy Valley via Glendhu with my brother George Lindsay Roberts (8 years 9 months old) who was to take my place at Heathfield when I went to the Flat.”
          </p>

          <p>
            “The first day I took George to the Greta Valley School, Mr John Barr, married roadman for the Waipara Road Board who lived in the Road Board cottage a little distance north of Mrs Gibb, was working on the rod on the Motunau hill, and he asked how old George was. When told he said he would be a good mate for his second daughter, Grace, who was a baby when Mr and Mrs Barr came to the Flat in the winter of 1886.” Grace died a young woman of appendicitis.
          </p>

          {/* Footnote 4 */}
          <div className="bg-[#f3ede0] p-4 rounded-xl border border-[#d8ceb9] text-sm text-[#5c4328] my-2">
            <p>
              <sup className="font-bold">4</sup> “A Personal History” pages 20-21
            </p>
          </div>

          <p>
            “During 1894 Miss Watson sometimes got me to teach a lower class their tables and I was quite handy with the long wooden pointer if I considered they were not paying attention or putting their hands on the desk. The teacher told me that was going a little too far as the parents of the children might object. As it was Mr McLelland complained (not publicly) that Miss Watson sat sewing or knitting while Eddie Roberts did the teaching.”
          </p>

          <p>
            “During 1895 while I Standard 2, for half an hour during the lunch break I did Standard 3 work, and also worked on in the evenings at Mrs Gibb’s and at the school examination in November of that year Miss Watson asked Mr Wood to examine me as a Standard 3 pupil and I then went into Standard 4.”
          </p>

          <p>
            Annie recalled that she had been encouraged to continue studying art and to do a lot more drawing by her schoolteacher, Miss Margaret Alice Carpenter.
          </p>
        </div>

        {/* Section Divider: Christmas Day 1899 as remembered by Uncle Ted Roberts */}
        <div className="pt-6 border-t-2 border-[#d8ceb9] space-y-6">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            CHRISTMAS DAY 1899 as remembered by Annie’s Uncle Ted Roberts, who was 19 years old at the time
          </h2>

          <p>
            Christmas Day they all went to Motunau Beach – and there must have been at least 10 horses there
          </p>

          <ul className="list-disc pl-6 space-y-2 text-[#2c221e]">
            <li>Mr &amp; Mrs Jack Barr and their family of six – Jennie, Grave, Melville, Victor, Lewis and Nellie.</li>
            <li>Morris Roberts who had been shearing for Stewart and George Gibb</li>
            <li>Jim and Lizzie Gibb and three children – Helen, Annie and Morris – drove down in their buggy drawn by a grey horse Jim had bought from the head shepherd at Tipapa</li>
            <li>Stewart and Jennie Gibb and four children – Burnie, Mildred, Frances and Daisy the baby – drove in a gig pulled by their reliable horse called Tim, from Glen Clova</li>
            <li>The grandparents, Robert and Sarah Roberts, drove from Plas Isa in their buggy with the members of the family who had not gone in another buggy driven by Baxter Roberts</li>
            <li>Margaret Roberts celebrated with the family for the day, from where she was working as a cook on Greta Peaks for a few months</li>
            <li>George Gibb rode his hack in</li>
            <li>Ted Roberts also rode a hack in</li>
            <li>Helen and Ella Gibb were there, and surprised to see young Ted as they didn’t know he had returned from Marlborough</li>
          </ul>

          <p className="font-bold text-[#4a3525] pt-2">
            More people came for the evening party at Heathfield.
          </p>

          <ul className="list-disc pl-6 space-y-2 text-[#2c221e]">
            <li>Angus Cameron who was engaged to the schoolteacher, Miss Carpenter</li>
            <li>George Wornall came with his violin</li>
            <li>Bill Shadrack the blacksmith, and several others</li>
          </ul>

          <p>
            Ted wrote of his activities after Christmas. First, he helped the blacksmith for three days until the new blacksmith arrived to help Bill Shadrack. Then he went to Glen Clova for a week to help his brother-in-law Stewart Gibb harvest grass seed from the hilly paddocks. They spread the grass seed on the floor of the wool shed and shearing boards to dry it out, turning it daily. Nearly 70 years later he remembered the nice cherry pie made with fruit Helen Gibb had sent to the farm. Ted got paid a pound for the week.
          </p>

          <p>
            After that he went home to help his father with the oat harvest, which Jim Gibb had cut with a binder machine – and got a surprise. His brother Tom had taken two days to cycle home from his farm near Ward. “He stayed at home for about a fortnight and then and my brother Morris left for Starborough via Christchurch and Lyttelton and then by sea via Wellington to Picton.” Ted’s next job was on Greta Peaks at 15 shillings a week and a hut to sleep in. He milked the cows, cut the firewood and attend to the hacks. He ate his meals in the kitchen where his sister Maggie was cook. They had wheat meal porridge and grilled chops for breakfast. It wasn’t long before he was helping with the muster.
          </p>

          <p>
            There never seemed to be any shortage of work for the Roberts children and they had an impressively wide range of skills by an early age.
          </p>
        </div>

        {/* Section Divider: Burnie Gibb's Letters */}
        <div className="pt-6 border-t-2 border-[#d8ceb9] space-y-6">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            BURNIE GIBB WROTE TO HIS COUSIN ANNIE
          </h2>

          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-2 border-[#d8ceb9] space-y-4 font-mono text-sm leading-relaxed text-[#2c221e]">
            <p className="font-bold">
              C.Coy<br />
              10th Reinforcements<br />
              Trentham April 30th 1916
            </p>
            <p>Dear Annie</p>
            <p>
              I only hope things are going as well for people as they are for us up here. You can tell Morris that he needn’t fret about the way he would be treated for it is as different from the Territorial System as cheese from chalk.
            </p>
            <p>
              We have had several holidays, first for races and then for Easter, and leave for about four hours at night is easily obtained.
            </p>
            <p>
              Wellington is now familiar to us, and we know a few people, one or two lady friends as well. There is a dance in the soldier’s club about every three days, I went to one, but there was a terrible rush for partners, and the room got very warm. But we had a good time, somehow when we are on leave, we always make an acquaintance to pass the time with.
            </p>
            <p>
              Last Friday evening we were invited to a party given by some friends of Doug Foster. There was a girl for each boy, and we did it well. Tommy Denton will call at Heathfield shortly after he comes home, how will that suit you, old girl? We are all close together up here though not in the same hut.
            </p>
            <p>
              Uncle will be in great form now he has more land, that is if the financial arrangements are suitable. Tell him that we start our shooting on Wednesday. They have all told about 125 targets on the ranges here, so they can handle a fair crowd of shooters.
            </p>
            <p>
              I have seen Dolly, known now as Annie Line. She is going home this week and hopes to come up to Motunau for a bit of a holiday. She is really a terrible girl for the boys, she has at least a dozen at or about Kaitoke where she worked.
            </p>
            <p>
              So far the drill has all been squad drill with and without rifles, now of course always with rifles.
            </p>
            <p>
              Oly and Doug are Sergeants now, Harry Johnson a Corporal, whilst your humble servant is still a Private. There is a terrific amount of liquor drunk by soldiers in Wellington when they are on leave. Hundreds come home nearly tight. The women who look after things at the soldier’s clubrooms are real heroes. They hear some awful language at times. We can get a meal there for nothing. With the best of wishes until next time
            </p>
            <p className="font-bold">Burney</p>
            <p className="italic">We go to Featherstone Friday week.</p>
          </div>

          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-2 border-[#d8ceb9] space-y-4 font-mono text-sm leading-relaxed text-[#2c221e]">
            <p className="font-bold">
              May 12th 1917<sup className="font-bold text-[#7a5b3a]">5</sup>
            </p>
            <p>Dear Annie</p>
            <p>
              The other day I got your tin of shortcake, and it was jolly good indeed. It’s very good of you to have sent me a parcel for I know you are pretty busy. Now if you are sending parcels to Morris, to find out how many go astray, you want to number them and keep a note, so you won’t make a mistake.
            </p>
            <p>
              I have had a lot of parcels from different people, but I only know of two that never reached me. Some only let on that they are sending one I suppose because they think I might be disappointed if it didn’t come. But I would like to know of everyone so that I would be certain how much of my stuff somebody else is getting.
            </p>
            <p>
              Times are a bit lively here now, sometimes there is shelling by both sides and things are only second class. You may have seen it in the paper that Germany is a bit short of shells, but they seem to have any amount here. I have seen the boys coming home at 3 in the morning after working all night. Said they were completely ragged. But they ran like hares when a shell or two came.
            </p>
            <p>
              The grass is green, the wheat is growing, the trees have their leaves, the fruit trees are all abloom, and if there was no war I could stop and admire the country’s beauty. But the last thing on Earth mars all. I forget everything and curse the whole business every time I see someone get hurt, especially when they stay here beneath a little wooden cross.
            </p>
            <p>
              And there are cemeteries everywhere, the little cross has on it one of the two most high honours a soldier can earn, killed in action or died of wounds. Well old lassie, I hope you are alright and somebody with you for company now the boy is gone into camp. I and the boys are all well so best luck to you and Uncle.
            </p>
            <p>Your affectionate Cousin</p>
            <p className="font-bold">15528 Burney</p>
          </div>

          <blockquote className="bg-[#f3ede0] p-6 rounded-2xl border-l-4 border-[#7a5b3a] text-sm md:text-base leading-relaxed text-[#2c221e] italic space-y-3">
            <p>
              “Times are quite a bit lively here now, sometimes there is shelling by both sides and things are only second class. You may have seen it in the papers that Germany is a bit short of shells, but they seem to have any amount here. I have seen the boys coming home at 3 in the morning after working all night. Said they were completely fagged. But they ran like hares when a shell or two came. The grass is green, the wheat is growing, the trees have most of their leaves, the fruit trees are in bloom, and if there was no war I would stop and admire the country’s beauty. But the last thing on earth mars all. I forget everything of course very time I see someone get hurt, especially when they stay here beneath a little wooden cross. And there are cemeteries everywhere, the little cross has on it one of the two most honours a soldier can earn “Killed in Action” or ”Died of Wounds”.
            </p>
            <footer className="text-xs font-bold font-sans text-[#7a5b3a] not-italic">
              From a letter Burnie wrote to his cousin Annie Gibb
            </footer>
          </blockquote>

          {/* Footnote 5 */}
          <div className="bg-[#f3ede0] p-4 rounded-xl border border-[#d8ceb9] text-sm text-[#5c4328] my-2">
            <p>
              <sup className="font-bold">5</sup> Burnie was killed 86 days later, 6 August 1917, at Messines Ridge (shelled)
            </p>
          </div>
        </div>

        {/* Section Divider: Morris Gibb's Letter */}
        <div className="pt-6 border-t-2 border-[#d8ceb9] space-y-6">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            THE LETTER MORRIS GIBB WRITE TO HIS SISTER, ANNIE, ABOUT BURNIE
          </h2>

          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-2 border-[#d8ceb9] space-y-4 font-mono text-sm leading-relaxed text-[#2c221e]">
            <p className="font-bold">8/11/17</p>
            <p>Dear Annie</p>
            <p>
              This is the first time I have started to write to you since coming over to France. If a chap in the tent had not managed to get measles, I would most likely have been up the line by now.
            </p>
            <p>
              As it is I will be here for three weeks at the least, perhaps longer. We see a fair few French people and what I have seen are a very poor class of people and also pretty dirty. Remember the Hubert’s? Fred and I have been together every night lately and I can tell you I was pleased to see him, it was a pleasant surprise one morning when I came face to face with him. We had our photo’s taken together last Sunday and Fred will get them in a few days.
            </p>
            <p>
              I am properly isolated this time we had to pull up our tent and shift right away from the rest of the camp so I may not see Fred before going up the line.
            </p>
            <p>
              I was very sorry to hear that Burnie was killed but accidents happen over here sometimes, pretty often in fact. Fred saw Dave and from what I can gather Dave and his party were being relieved by the lot Burnie was in when he (suppose others were with him) was blown up by a shell. I was pleased someone who knew him was there and I hope Dave has written to them at Glen-Clova as he knows more about it more than anyone.
            </p>
            <p>
              I hope Dad is right again now as it would be very awkward for him with a poisoned hand this time of the year.
            </p>
            <p>
              Well Annie, shearing will be over and the harvest well in by the time you get this. This is all I can say just now.
            </p>
            <p>I remain</p>
            <p className="font-bold">
              Your loving brother<br />
              Morris
            </p>
          </div>
        </div>

        {/* Section Divider: Ted Roberts & Annie Denton Correspondence */}
        <div className="pt-6 border-t-2 border-[#d8ceb9] space-y-6">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            TED ROBERTS AND HIS NIECE ANNIE DENTON CORRESPONDED FOR DECADES
          </h2>

          {/* Image 83 */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/83 Annie_s Uncle Ted Roberts.jpg"
              alt="Annie's Uncle Ted Roberts"
              caption="Annie’s Uncle Ted"
            />
          </div>

          {/* Letter 1: 7 June 1958 */}
          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-2 border-[#d8ceb9] space-y-4 font-mono text-sm leading-relaxed text-[#2c221e]">
            <p className="font-bold">
              Riverside P. Bag. Culverden<br />
              7 June 1958
            </p>
            <p>Dear Annie and Tom</p>
            <p>
              Thank you for your donation of 2 pounds towards buying a fridge for your Aunt Margaret<sup className="font-bold text-[#7a5b3a]">6</sup>. The birthday party is to be at Will and Daisy Elliott's, 103 Sawyers Arms Road, just on the west side of the railway line. The date is Saturday, 26 July 1958 and the time about 2pm.
            </p>
            <p>
              Ian Fordyce, Margaret and three children were here last weekend for tea, Queen’s Birthday. The baby born 27 April is doing well and weighs 10lbs. He weighed 7lbs 3ozs at birth. I told Ian he could buy the fridge as soon as he liked, and I would pay for it in the meantime. Only four people so far have sent in donations. One was Laila Grubb<sup className="font-bold text-[#7a5b3a]">7</sup> whom Eva happened to meet one day in Culverden. Leila and Tony were on their way back to Cape Foulwind. Their son Clayton is in the forestry department at Golden Downs, Nelson Province. Eva just happened to mention the matter to Leila, and she responded on the spot with a donation. I have written a good many letters to different ones on the subject and still have a lot more to write.
            </p>
            <p>
              We hope you and Tom enjoyed the change away from home when you went to South Canterbury. I understand it was to Ranfurly. The Popplewell’s<sup className="font-bold text-[#7a5b3a]">8</sup> came from there to here 50 years ago this coming spring. I arrived early in July. Grandfather would be only 50 at the time and your uncle George 43, and Stewart 44. Now my son Edward is 45 and I am 77. There is little we can do about it except carry on.
            </p>
            <p>
              It was 45 years last Sunday since George Roberts<sup className="font-bold text-[#7a5b3a]">9</sup> rode up from Scargill on Tom's chestnut horse to look after the place while I went up to Marlborough by boat. I had not been back since coming down in July 1908. The days of the week are the same this year as then, first of June came on a Sunday.
            </p>
            <p>
              I hope your neuritis has cleared up by now. I suppose it would be Dr. Volkman you consulted. I used to do work for the old Dr. Volkman at Blind River and he officiated at the births of Ethel and Gordon Roberts<sup className="font-bold text-[#7a5b3a]">10</sup>. He would not give mothers an anaesthetic at childbirth.
            </p>
            <p>
              Thank you for giving me Helen's address. I shall write to her and get the letters away Monday.
            </p>
          </div>

          {/* Image 84 */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/84 example of letter.jpg"
              alt="Example of letter"
              caption="Example of letter"
            />
          </div>

          {/* Letter 1 Continuation */}
          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-2 border-[#d8ceb9] space-y-4 font-mono text-sm leading-relaxed text-[#2c221e]">
            <p>
              … A heavy cold rain is the worst thing on newly shorn shape, and if it happens after hot weather, losses are sure to occur. In winter the sheep are not sweating in the wool before shearing so don't feel the contrast so much as in summer. I have nearly 60 acres sown in wheat, but no oats sown so far. I am fairly heavily stocked with sheep at present but will sell the wethers<sup className="font-bold text-[#7a5b3a]">11</sup> about the end of July or sooner if the price improves.
            </p>
            <p>
              If we don't meet sooner, we hope to meet you and Tom at Elliott's on 26 July. I sometimes see Jack Denton in Culverden, he is a little bent now and is showing his age.
            </p>
            <p>Thanking you again</p>
            <p className="font-bold">Love from Uncle Ted and Eva.</p>
            <hr className="border-[#d8ceb9]" />
            <p>
              Here is a little extra to my letter. A man named Jack Gibson has a farm at Shenandoah, on the Maruia River over the Lewis Pass, a little over 100 miles from here. Occasionally he and his wife, who is a neatly dressed woman, leave home with a load of pigs at 3 or 4 in the morning and are in Addington in time to sell them in the market. They arrive back at my place about three in the afternoon, have afternoon tea and take 50 bales of hay, fowl wheat, oats or dry grass seed home with them the same day.
            </p>
            <p>
              Eddie Trueman knew his mother on the Picton Road as a young woman. She was a Mrs Paton and Eddie has her photo in the old family album. She and one daughter were buried alive when the 1929 Murchison earthquake<sup className="font-bold text-[#7a5b3a]">12</sup> caused the side of the hill to give way and completely covered the house. Another daughter had taken soup to a sick woman who lived some distance away and she escaped. Moral, be helpful to those in sickness or distress.
            </p>
            <p className="font-bold">E.R</p>
          </div>

          {/* Footnotes 6-12 */}
          <div className="bg-[#f3ede0] p-4 rounded-xl border border-[#d8ceb9] text-sm text-[#5c4328] space-y-1 my-2">
            <p><sup className="font-bold">6</sup> Maggie never married, and when he mother died she inherited the house at 137 Champion St, Christchurch</p>
            <p><sup className="font-bold">7</sup> Laila Grubb was the daughter of Ted’s brother T.E.L. Roberts (Tom)</p>
            <p><sup className="font-bold">8</sup> Popplewell was the kindly local Minister</p>
            <p><sup className="font-bold">9</sup> George was killed in a dray accident on his 28th birthday 1914</p>
            <p><sup className="font-bold">10</sup> Children of Morris and Phoebe Roberts</p>
            <p><sup className="font-bold">11</sup> Wethers are castrated male sheep (usually done by rubber rings at an early age)</p>
            <p><sup className="font-bold">12</sup> 17 June 1929, 7.3, shaking 4 minutes, 17 killed</p>
          </div>

          {/* Image 85 */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/85 Murchhison Earthquake.jpg"
              alt="Murchison Earthquake"
              caption="Murchison Earthquake"
            />
          </div>

          {/* Letter 2: 5 Nov 1964 */}
          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-2 border-[#d8ceb9] space-y-4 font-mono text-sm leading-relaxed text-[#2c221e]">
            <p className="font-bold">
              Riverside, R.D. Culverden<br />
              5 Nov 1964
            </p>
            <p>Dear Annie and Tom</p>
            <p>
              It is 70 years today that I left Heathfield to go and live at the Flat<sup className="font-bold text-[#7a5b3a]">13,14</sup>and go to school from there, I was not consulted about the change. It was requested by old Mrs. Gibb and your father and mother did not like to refuse him, and my parents agreed to it. George who was about eight years and nine months old at the time took my place at Heathfield and remained there till the family removed from Happy Valley to Scargill.
            </p>
            <p>
              Eva and I were nearly down to your place on Monday 19th Oct. We attended the funeral of George Blackmore, first at the service in the Methodist Church at the corner of Brougham Street and Colombo Street in Sydenham, and then in the cemetery near Leeston. He was buried alongside of his wife Margaret Agnes Blackmore who died 14 November 1946. She had evidently lived in the Leeston district…..
            </p>
          </div>

          {/* Image 86 */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/86 letter continuation.jpg"
              alt="Letter continuation"
              caption="Letter continuation"
            />
          </div>

          {/* Letter 2 Continuation */}
          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-2 border-[#d8ceb9] space-y-4 font-mono text-sm leading-relaxed text-[#2c221e]">
            <p>
              ……I did not see him after George and I left Southland in 1905, until the autumn of 1936. He then had a farm at Westerfield out from Ashburton and had been to Hanmer for treatment. At the end of the treatment, he came and stayed a day with me before going home to his wife. They had no children.
            </p>
            <p>
              Of late years he was living retired in Barrington Street at ChCh and had been on crutches for some years, though he could drive his car, breaking his wrist a while ago. He was in Princess Margaret Hospital, and the doctors operated on his hip to help his arthritis, and he was transferred to Burwood to recover. We called to see him there soon after his arrival. He must have been failing instead of improving for Burt’s wife came up to see him and he was cheerful enough, but the same evening the hospital rang to say he had died. He was 85, and in my opinion, too old for operations. Burt is older and Charlie Blackmore who married a Miss Sheat is nearly 90.
            </p>
            <p>
              As we were going from home and back in the day, though missed you, did not have time to call. Likely we will see you at the Gibb wedding at St Andrews Chapel on Nov 28th. It is possible we may go to Ward next week. Eva wrote to Fanny<sup className="font-bold text-[#7a5b3a]">15</sup> yesterday to see to see if she would like to go with us. We took her up in October 1954 to Margi’s 80th birthday party, and Ruby asked us to take her again. Eva had seven days in bed a while ago, with some flu or other, since October 19th.
            </p>
            <p>
              Ailsa and baby (Robert Bruce) are doing well. The boy was baptized on Sunday 27 Sep. he is being breast-fed and only cries when he needs changing. Ailsa intends to wean him before she and Bruce go to Picton after Christmas, when Bruce competes in some of the rowing events. Ailsa has arranged to leave the baby with a woman in Halswell who caters for such work. Eva has a cousin in Blenheim who cares for children while their parents are away for short periods.
            </p>
            <p>
              My garden is in better order than for some time. Broad beans and peas are in full bloom, potatoes and onions doing well and we have been using asparagus for some time. Since last Saturday we have had 135 points of rain<sup className="font-bold text-[#7a5b3a]">16</sup> and still need more, but the backup has saved the situation for the present.
            </p>
            <p>
              Edward got 50 lambs away yesterday. About half of them Corriedale wether lambs. Eva and I went to town on Tuesday and coming home in the late afternoon met ten or a dozen trucks going to the works with lambs, and likely a lot had gone earlier in the day. Lambs have been doing well.
            </p>
            <p>
              Eva and I sent 10 different parcels of butter and cheese to friends and relations in Sydney and Scotland. They will receive them in time for Christmas. Tell me about pheasants and partridges when you write. We had a white- faced Heron bird this week
            </p>
            <p className="font-bold">Uncle Ted</p>
          </div>

          {/* Footnotes 13-16 */}
          <div className="bg-[#f3ede0] p-4 rounded-xl border border-[#d8ceb9] text-sm text-[#5c4328] space-y-1 my-2">
            <p><sup className="font-bold">13</sup> Cabbage Tree Flat, at Helen Gibb’s request</p>
            <p><sup className="font-bold">14</sup> His daughter Ailsa Fraser</p>
            <p><sup className="font-bold">15</sup> His sister, Fanny Craighead</p>
            <p><sup className="font-bold">16</sup> 100 points of rain = 25,4 mm</p>
          </div>

          {/* Letter 3: 15 Nov 1966 */}
          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-2 border-[#d8ceb9] space-y-4 font-mono text-sm leading-relaxed text-[#2c221e]">
            <p className="font-bold">
              Riverside, R.D. Culverden<br />
              15 Nov 1966
            </p>
            <p>Dear Annie</p>
            <p>
              Pleased to get your second later yesterday reporting an improvement in the condition of your sister Helen. After getting rid of all the stones from her liver she should make a steady recovery. Any operation takes time for the body to get back to normal.
            </p>
            <p>
              Likely my sister Margaret will return to ChCh early next week, as she will run out of pills about then. Eva is sending you voting papers today, so that she can record her vote before leaving here. For my part I want to see the National Government returned.<sup className="font-bold text-[#7a5b3a]">17</sup> Taxation is bad enough under the present Government but by all the promises made by the Labour Party, will require a great deal of extra money to be fulfilled if that party is elected on Nov 26th. Land prices are too high and those with fair sized freehold farms find rates and bank tax a heavy drain on the income.
            </p>
            <p>
              Edward<sup className="font-bold text-[#7a5b3a]">18</sup> went to town yesterday and called on the Vicar of St Peter's (the Rev Mr Keith) Upper Riccarton, the same church where my parents were married Christmas Eve 1866. Edward arranged with the Vicar to hold a short service in the church at 11 o'clock Saturday morning 3 Dec to celebrate the first 100 years of the wedding. It would not be convenient to have the gathering on the exact date of the wedding, and the 3 Dec was a free date for the Vicar to take the service.
            </p>
            <p>
              The St Peter’s Parish Hall will hold 200 and contains facilities for hot water and cups. Probably those attending may be asked to bring some eatables, but Eva and Edward thought it could be arranged to have sandwiches supplied from some firm near at hand.
            </p>
            <p>
              Edward thought probably 70 people would attend, or even more. He is starting proceedings today by going to Culverden to arrange about having notices printed.
            </p>
            <p>
              At the church service a collection will be taken in aid of the National Council of Churches work. You will hear more about the event before it takes place.
            </p>
            <p>
              You mentioned your two goats getting over fences, I actually saw one in Marlborough standing on top of a fence post.
            </p>
            <p>
              Margaret is staying with us till after the election. We will return her to the home a few days before the 3rd Dec, so that she can have a period of rest before the gathering of the clan.
            </p>
            <p className="font-bold">Uncle Ted, E. Roberts</p>
          </div>

          {/* Footnotes 17-18 */}
          <div className="bg-[#f3ede0] p-4 rounded-xl border border-[#d8ceb9] text-sm text-[#5c4328] space-y-1 my-2">
            <p><sup className="font-bold">17</sup> National Party was returned for 3rd consecutive term, Prime Minister Sidney Holland</p>
            <p><sup className="font-bold">18</sup> His son, Edward T.</p>
          </div>

          {/* Letter 4: 13 Aug 1967 */}
          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-2 border-[#d8ceb9] space-y-4 font-mono text-sm leading-relaxed text-[#2c221e]">
            <p className="font-bold">
              Riverside, R.D. Culverden<br />
              13 Aug 1967
            </p>
            <p>Dear Annie</p>
            <p>
              It is a long time since we heard anything of you and Tom, so perhaps you will write a few lines when time permits.
            </p>
            <p>
              Edward told us on Friday evening that he had a letter from his son Richard who is teaching at Oxford District High School, saying he was going to the Solomon Islands<sup className="font-bold text-[#7a5b3a]">19</sup> in January to teach. Probably as a missionary teacher. He is taking the place of Mountford who has been there some years and has a family of 5 children. The Islands lie up next to the Equator and eastward of New Guinea. There are eight islands in the group.
            </p>
            <p>
              Marc Roberts is engaged to Janice Hill, a cousin of the girl Kevin married. At present she is teaching at Te Awamutu High School in the Waikato. I think they will be getting married after harvest. Marc has over 1000 acres of heading booked up after the season starts. If some of the bookings are for grass seed the season will commence early. Unless more rain does fall than so far this year, there won't be much to head.
            </p>
            <p>
              We have had NW winds for 3 days running. One thing about the dry weather, it has been favourable for lambing which started about the middle of July. We went to Ward on 3 July and came home on 17th, after sleeping every night at the home of Frank and Ruby. While in Marlborough we saw most of our friends and relatives. Ruby gave Eva<sup className="font-bold text-[#7a5b3a]">20</sup> several good dresses for your Aunt Margaret. Eva fitted them on Thursday and brought three of them home to be altered.
            </p>
            <p>
              We are to go to town this coming Friday to bring home three of our grandchildren from Brookside Terrace. The other two children are to stay with Ailsa while their parents drive around the Haast Pass Road.
            </p>
            <p>
              An article I was asked to write on Pukeko in North Canterbury appeared in the last issue of the Forest and Bird Protection Society’s magazine. After reading it, Mrs. MacDonald of Woodend, who was a daughter of Sir George and Lady Clifford, wrote and told me some swamp hens on their property came and fed with her ducks and took no notice of her presence, but was shy of strangers. Forest and Bird are trying to get a count of Pukeko throughout the country and sent me a form to fill in. I wrote and gave the names of people on whose land Pukeko existed and advised writing to them for information as to whether the birds were decreasing or increasing. It is out of the question for me to go out to count birdlife.
            </p>
            <p>
              While there, Nellie and Malvern Barr, who live in Cambridge about 11 miles from John Ford<sup className="font-bold text-[#7a5b3a]">21</sup>'s home, came to see us. John drove them home again. Nelly was born 21 June 1894. Malvern, who now calls himself Jack, will be 80 in Dec. John Ford is back in his former job as an arts and crafts instructor. He has been selling some of his drawings and paintings at prices up to 50 pounds. Every now and then Eva sends about 6 dozen eggs to Helen by airfreight. On our return from Hamilton, we stayed three days with Daphne and Norman. Daphne has since had a combined operation for hernia and gallstones. How are pheasants and partridges doing in your district?
            </p>
            <p className="font-bold">Uncle Ted and Auntie Eva</p>
          </div>

          {/* Footnotes 19-21 */}
          <div className="bg-[#f3ede0] p-4 rounded-xl border border-[#d8ceb9] text-sm text-[#5c4328] space-y-1 my-2">
            <p><sup className="font-bold">19</sup> Richard taught at Pawa Secondary School 1968 - 69</p>
            <p><sup className="font-bold">20</sup> Eva was an expert dressmaker</p>
            <p><sup className="font-bold">21</sup> John was Ted’s son in law, married to Helen</p>
          </div>

          {/* Image 87 */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/87 Annie Denton in  her 80_s.jpg"
              alt="Annie Denton in her 80s"
              caption="Annie in her 80’s"
            />
          </div>
        </div>

        {/* Section Divider: Richard Lovell Roberts */}
        <div className="pt-6 border-t-2 border-[#d8ceb9] space-y-6">
          <h2 className="font-serif text-2xl md:text-3xl font-bold text-[#2c221e]">
            RICHARD LOVELL ROBERTS
          </h2>

          <p>
            Ted’s grandson Richard Lovell Roberts found Solomon Islands to be wonderfully varied, rich in flora and fauna, and very interesting. He had a Japanese neighbour he taught English, and from whom he learnt some Japanese, which resulted in Richard studying the Japanese language at university on his return to NZ.
          </p>

          {/* Image 88 */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/88 1968 Chinese trading boats in the Solomon Islands.jpg"
              alt="1968 Chinese trading boats in the Solomon Islands"
              caption="Richard remembered the little Chinese trading boats which used to chug around picking up copra and selling goods such as stick tobacco and corned beef – idyllic. 1968-69"
            />
          </div>

          <p>
            Richard wrote later about Mr Willy Cross, a local farmer named after the Melanesian Mission ship he was born on, MV Southern Cross. Willy Cross asked a question about the moon landing (20 July 1969). It intrigued Richard, and years later, in 2013, he finished his reflections. These are the first verses only.
          </p>

          {/* Image 89 */}
          <div className="my-6">
            <ArchivalImageCard
              src="/images/89 Willy Cross.jpg"
              alt="Mr Willy Cross"
              caption="Mr Willy Cross"
            />
          </div>

          {/* Poem Block */}
          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] font-serif text-sm md:text-base leading-relaxed text-[#2c221e] italic space-y-3">
            <p>
              Mr Willy Cross came to barter taro for stick tobacco<br />
              -the staff of life for simple pleasure;<br />
              And seek further information to morning news<br />
              On “tok=tok pijin” radio.<br />
              Watpo disfala waitman go long mum wea no wara? Nogut ples long garden!” which means “Why go walkabout so far away when the substance of our wealth and treasure is discovered here in our gardens and the sea?”
            </p>
            <p>
              Ah, the security and peace of mind,<br />
              The bliss of innocence that isolation brings<br />
              Far away from Cold War fears, International politics, and other things<br />
              Unhinging “civilised” mankind!
            </p>
          </blockquote>

          <p>
            When our early family members sailed to the southern oceans, they would have had no concept of the future geo-political sphere the new country would become part of. Like Mr Willy Cross, they created their own security and peace of mind in the relative isolation of a young country.
          </p>
        </div>

        {/* Navigation Footer */}
        <div className="pt-8 border-t-2 border-[#d8ceb9] flex flex-wrap items-center justify-between gap-4 text-xs font-bold font-serif text-[#7a5b3a]">
          <button
            onClick={() => onSelectPart && onSelectPart('part4')}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#f3ede0] hover:bg-[#e8dec9] border border-[#d8ceb9] transition cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Part 4: John Frederick &amp; Eliza</span>
          </button>

          <button
            onClick={() => onSelectPart && onSelectPart('part6')}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white transition shadow-xs cursor-pointer ml-auto"
          >
            <span>Next Chapter: Part 6 (Isaac Ephraim Denton)</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

      </article>

    </div>
  );
};
